import { Menu } from "../../ui/Menu";

type Props = {
  busy: boolean;
  imgBusy: boolean;
  exporting: "pdf" | "pptx" | null;
  canUndo: boolean;
  canRedo: boolean;
  showGuides: boolean;
  selectedCount: number;
  singleImageLayerSelected: boolean;
  singleGraphicLayerSelected: boolean;
  singleBakedImageSelected: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onAddText: () => void;
  onAddShape: () => void;
  onAddImage: () => void;
  onAddRasterIllustration: () => void;
  onAddTable: () => void;
  onAddChart: () => void;
  onAddGraphic: () => void;
  onRegenerateGraphic: () => void;
  onGenerateLayerImage: () => void;
  onGenerateSlideVisual: () => void;
  onGenerateAllVisuals: () => void;
  onAutofitSlide: () => void;
  onAutofitAll: () => void;
  onRegenerateAllTextRasters: () => void;
  onReorganize: () => void;
  onAlign: (mode: "left" | "center-h" | "right" | "top" | "middle-v" | "bottom") => void;
  onDistribute: (axis: "horizontal" | "vertical") => void;
  onFitSafe: () => void;
  onDelete: () => void;
  onExportPdf: () => void;
  onExportPdfWithNotes?: () => void;
  onExportPptx: () => void;
  onToggleGuides: (v: boolean) => void;
  onShowOverview: () => void;
};

export function EditorToolbar({
  busy,
  imgBusy,
  exporting,
  canUndo,
  canRedo,
  showGuides,
  selectedCount,
  singleImageLayerSelected,
  singleGraphicLayerSelected,
  singleBakedImageSelected,
  onUndo,
  onRedo,
  onAddText,
  onAddShape,
  onAddImage,
  onAddRasterIllustration,
  onAddTable,
  onAddChart,
  onAddGraphic,
  onRegenerateGraphic,
  onGenerateLayerImage,
  onGenerateSlideVisual,
  onGenerateAllVisuals,
  onAutofitSlide,
  onAutofitAll,
  onRegenerateAllTextRasters,
  onReorganize,
  onAlign,
  onDistribute,
  onFitSafe,
  onDelete,
  onExportPdf,
  onExportPdfWithNotes,
  onExportPptx,
  onToggleGuides,
  onShowOverview,
}: Props) {
  const locked = busy || imgBusy;

  return (
    <div className="editor-toolbar editor-toolbar--grouped" role="toolbar" aria-label="Strumenti editor">
      <button type="button" disabled={!canUndo || locked} onClick={onUndo} aria-label="Annulla">
        Annulla
      </button>
      <button type="button" disabled={!canRedo || locked} onClick={onRedo} aria-label="Ripeti">
        Ripeti
      </button>

      <Menu
        label="Inserisci"
        disabled={locked}
        items={[
          { id: "text", label: "+ Testo", onClick: onAddText },
          { id: "shape", label: "+ Forma", onClick: onAddShape },
          { id: "image", label: "+ Immagine", onClick: onAddImage },
          { id: "raster", label: "+ Illustrazione raster", onClick: onAddRasterIllustration },
          { id: "table", label: "+ Tabella", onClick: onAddTable },
          { id: "chart", label: "+ Grafico", onClick: onAddChart },
          { id: "graphic", label: "+ Illustrazione", onClick: onAddGraphic },
        ]}
      />

      <Menu
        label="AI"
        disabled={imgBusy}
        items={[
          {
            id: "graphic",
            label: "Rigenera illustrazione AI",
            disabled: !singleGraphicLayerSelected,
            onClick: onRegenerateGraphic,
          },
          {
            id: "layer-img",
            label: singleBakedImageSelected ? "Rigenera illustrazione" : "Genera immagine widget",
            disabled: !singleImageLayerSelected,
            onClick: onGenerateLayerImage,
          },
          { id: "slide-visual", label: "Genera immagine (slide)", onClick: onGenerateSlideVisual },
          { id: "all-visuals", label: "Genera immagini (FIFO)", onClick: onGenerateAllVisuals },
          { id: "autofit-slide", label: "Adatta automaticamente (slide)", onClick: onAutofitSlide },
          { id: "autofit-all", label: "Adatta automaticamente (tutte)", onClick: onAutofitAll },
          {
            id: "text-raster-all",
            label: "Rigenera illustrazioni testo",
            onClick: onRegenerateAllTextRasters,
          },
          {
            id: "reorganize",
            label: "Riorganizza testo",
            disabled: locked || !selectedCount,
            onClick: onReorganize,
          },
        ]}
      />

      <Menu
        label="Allinea"
        disabled={selectedCount < 2}
        items={[
          { id: "left", label: "Sinistra", onClick: () => onAlign("left") },
          { id: "center-h", label: "Centro orizzontale", onClick: () => onAlign("center-h") },
          { id: "right", label: "Destra", onClick: () => onAlign("right") },
          { id: "top", label: "Alto", onClick: () => onAlign("top") },
          { id: "middle-v", label: "Centro verticale", onClick: () => onAlign("middle-v") },
          { id: "bottom", label: "Basso", onClick: () => onAlign("bottom") },
          {
            id: "dist-h",
            label: "Distribuisci orizzontale",
            disabled: selectedCount < 3,
            onClick: () => onDistribute("horizontal"),
          },
          {
            id: "dist-v",
            label: "Distribuisci verticale",
            disabled: selectedCount < 3,
            onClick: () => onDistribute("vertical"),
          },
          {
            id: "fit-safe",
            label: "Adatta safe zone",
            disabled: !selectedCount,
            onClick: onFitSafe,
          },
        ]}
      />

      <button type="button" onClick={onShowOverview}>
        Panoramica
      </button>

      <button
        type="button"
        className="danger-link"
        disabled={!selectedCount}
        onClick={onDelete}
        title="Elimina selezione"
      >
        Elimina{selectedCount > 1 ? ` (${selectedCount})` : ""}
      </button>

      <Menu
        label="Esporta"
        disabled={exporting !== null || locked}
        items={[
          {
            id: "pdf",
            label: exporting === "pdf" ? "Export PDF…" : "Export PDF",
            onClick: onExportPdf,
          },
          ...(onExportPdfWithNotes
            ? [
                {
                  id: "pdf-notes",
                  label: exporting === "pdf" ? "Export PDF…" : "PDF con note speaker",
                  onClick: onExportPdfWithNotes,
                },
              ]
            : []),
          {
            id: "pptx",
            label: exporting === "pptx" ? "Export PPTX…" : "Export PPTX",
            onClick: onExportPptx,
          },
        ]}
      />

      <label className="row editor-toolbar-guides">
        <input
          type="checkbox"
          checked={showGuides}
          onChange={(e) => onToggleGuides(e.target.checked)}
        />
        Guide
      </label>
    </div>
  );
}
